import { RateInterval } from '__types__/enums';
import { formatMoneyAmount, spokenMoney } from 'v5/core/money/formatMoney';
import { RATE_FEES } from 'v5/core/rates/resourceRate';
import type { RateFee } from 'v5/core/rates/resourceRate';
import type { RateFormValue } from 'v5/core/entity-values/values.types';

/** Words `describeRate` needs, in the user's language and locale. */
export type RateDescriptionCopy = {
  locale: string;
  /** "/ day" — after an amount. */
  unitShort(interval: RateInterval): string;
  /** "per day" — for screen readers. */
  unitSpoken(interval: RateInterval): string;
  /** "+15% agency". */
  feeSummary(percent: number, fee: RateFee): string;
  /** "plus 15% agency fee and 3% payroll fee". */
  feesSpoken(fees: ReadonlyArray<{ percent: number; fee: RateFee }>): string;
  noAmount: string;
  unnamed: string;
};

/**
 * One rate as the list shows it: "Day rate · CAD 850 / day · +15% agency",
 * with what a screen reader should say instead of the abbreviations.
 */
export type RateDescription = {
  name: string;
  money:
    | { type: 'none' }
    | {
        type: 'amount';
        currency: string;
        amount: string;
        unit: string;
        spoken: string;
      };
  /** Only fees above zero, in the order bookings apply them. */
  fees: ReadonlyArray<{ fee: RateFee; percent: number; text: string }>;
  feesSpoken: string | null;
  note: string;
};

export function describeRate(
  value: RateFormValue,
  copy: RateDescriptionCopy
): RateDescription {
  const fees = RATE_FEES.flatMap(fee => {
    const percent = value[fee.column];
    return percent === null || !(percent > 0)
      ? []
      : [{ fee, percent, text: copy.feeSummary(percent, fee) }];
  });
  const name = value.name.trim();
  return {
    name: name === '' ? copy.unnamed : name,
    money:
      value.amount === null || !Number.isFinite(value.amount)
        ? { type: 'none' }
        : {
            type: 'amount',
            currency: value.currency,
            amount: formatMoneyAmount(value.amount, copy.locale),
            unit: copy.unitShort(value.interval),
            spoken: `${spokenMoney(value.amount, value.currency, copy.locale)} ${copy.unitSpoken(value.interval)}`
          },
    fees,
    feesSpoken: fees.length === 0 ? null : copy.feesSpoken(fees),
    note: value.description.trim()
  };
}

/** "GBP 1,200 / day · +20% agency": money and fees on one line, no name. */
export function rateSummaryLine(
  rate: RateDescription,
  copy: Pick<RateDescriptionCopy, 'noAmount'>
): string {
  const money =
    rate.money.type === 'none'
      ? copy.noAmount
      : [rate.money.currency, rate.money.amount, rate.money.unit]
          .filter(part => part !== '')
          .join(' ');
  return [money, ...rate.fees.map(fee => fee.text)].join(' · ');
}
